<template>
  <div class="odontogram-form">
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p class="loading-text">Carregando odontograma...</p>
      </div>
    </div>

    <div class="form-group general-notes-section">
      <label class="form-label text-bold">Anotações gerais</label>
      <st-editor
        class="content-editor"
        id="general-notes"
        v-model="generalNotes"
        @input="onGeneralNotesChange"
      />
    </div>

    <div class="divider" />

    <div class="odontograma-container">
      <div class="tools-sidebar">
        <div class="tool-group">
          <div class="tool-buttons">
            <button
              class="tool-btn tooltip tooltip-right"
              :class="{ active: selectedTool === 'healthy' }"
              @click="selectTool('healthy')"
              data-tooltip="Sadio - Dente em perfeito estado"
            >
              <img :src="toothHealthy" alt="Sadio" class="tool-icon" />
            </button>
            <button
              class="tool-btn tooltip tooltip-right"
              :class="{ active: selectedTool === 'carious' }"
              @click="selectTool('carious')"
              data-tooltip="Cariado - Dente com cárie"
            >
              <img :src="toothCarious" alt="Cariado" class="tool-icon" />
            </button>
            <button
              class="tool-btn tooltip tooltip-right"
              :class="{ active: selectedTool === 'restored' }"
              @click="selectTool('restored')"
              data-tooltip="Restaurado - Dente com restauração"
            >
              <img :src="toothRestored" alt="Restaurado" class="tool-icon" />
            </button>
            <button
              class="tool-btn tooltip tooltip-right"
              :class="{ active: selectedTool === 'crown' }"
              @click="selectTool('crown')"
              data-tooltip="Coroa - Dente com coroa"
            >
              <img :src="toothCrown" alt="Coroa" class="tool-icon" />
            </button>
            <button
              class="tool-btn tooltip tooltip-right"
              :class="{ active: selectedTool === 'canal' }"
              @click="selectTool('canal')"
              data-tooltip="Canal - Dente com tratamento de canal"
            >
              <img :src="toothRootCanal" alt="Canal" class="tool-icon" />
            </button>
            <button
              class="tool-btn tooltip tooltip-right"
              :class="{ active: selectedTool === 'missing' }"
              @click="selectTool('missing')"
              data-tooltip="Ausente - Dente ausente"
            >
              <img :src="toothMissing" alt="Ausente" class="tool-icon" />
            </button>
          </div>
        </div>
      </div>

      <div class="main-odontograma">
        <div class="dental-chart">
          <div class="dental-arch upper-arch">
            <tooth-item
              v-for="tooth in upperTeeth"
              :key="tooth.number"
              :tooth="tooth"
              :tooth-surfaces="toothSurfaces"
              :is-selected="selectedTeeth.some((t) => t.number === tooth.number)"
              @tooth-selected="selectTooth"
              @tooth-clicked="openToothDetails"
              @surface-clicked="toggleSurface"
            >
            </tooth-item>
          </div>
          <div class="dental-arch lower-arch">
            <tooth-item
              v-for="tooth in lowerTeeth"
              :key="tooth.number"
              :tooth="tooth"
              :tooth-surfaces="toothSurfaces"
              :is-selected="selectedTeeth.some((t) => t.number === tooth.number)"
              @tooth-selected="selectTooth"
              @tooth-clicked="openToothDetails"
              @surface-clicked="toggleSurface"
            >
            </tooth-item>
          </div>

          <div class="deciduous-teeth upper-deciduous">
            <tooth-item
              v-for="tooth in upperDeciduous"
              :key="tooth.number"
              :tooth="tooth"
              :tooth-surfaces="toothSurfaces"
              :is-selected="selectedTeeth.some((t) => t.number === tooth.number)"
              @tooth-selected="selectTooth"
              @tooth-clicked="openToothDetails"
              @surface-clicked="toggleSurface"
            >
            </tooth-item>
          </div>

          <div class="deciduous-teeth lower-deciduous">
            <tooth-item
              v-for="tooth in lowerDeciduous"
              :key="tooth.number"
              :tooth="tooth"
              :tooth-surfaces="toothSurfaces"
              :is-selected="selectedTeeth.some((t) => t.number === tooth.number)"
              @tooth-selected="selectTooth"
              @tooth-clicked="openToothDetails"
              @surface-clicked="toggleSurface"
            >
            </tooth-item>
          </div>
        </div>
      </div>
      <div class="details-sidebar">
        <div class="tooth-details" v-if="selectedTeeth.length > 0">
          <div class="details-header">
            <h4 class="details-title">
              {{
                selectedTeeth.length === 1
                  ? `Dente ${selectedTeeth[0].number}`
                  : `${selectedTeeth.length} dentes selecionados`
              }}
            </h4>
          </div>
          <div class="details-form">
            <div class="form-group" v-if="selectedTeeth.length === 1">
              <label class="form-label">Condição</label>
              <select
                class="form-select"
                v-model="selectedTeeth[0].condition"
                @change="saveToothData(selectedTeeth[0])"
                :key="`condition-${selectedTeeth[0].number}-${selectedTeeth[0].condition}`"
              >
                <option value="">Selecione uma condição...</option>
                <option value="healthy">Sadio</option>
                <option value="carious">Cariado</option>
                <option value="restored">Restaurado</option>
                <option value="missing">Ausente</option>
                <option value="crown">Coroa</option>
                <option value="canal">Canal</option>
              </select>
            </div>
            <div class="form-group" v-if="selectedTeeth.length > 1">
              <label class="form-label">Aplicar condição a todos os dentes</label>
              <select
                class="form-select"
                v-model="selectedTeeth[0].condition"
                @change="saveAllSelectedTeeth"
                :key="`multi-condition-${selectedTeeth.length}`"
              >
                <option value="">Selecione uma condição...</option>
                <option value="healthy">Sadio</option>
                <option value="carious">Cariado</option>
                <option value="restored">Restaurado</option>
                <option value="missing">Ausente</option>
                <option value="crown">Coroa</option>
                <option value="canal">Canal</option>
              </select>
            </div>
            <div class="form-group" v-if="selectedTeeth.length === 1">
              <label class="form-label">Faces do dente</label>
              <div class="surface-options">
                <label class="surface-option" v-for="surface in toothSurfaces" :key="surface">
                  <input
                    type="checkbox"
                    :checked="
                      selectedTeeth[0].surfaces && selectedTeeth[0].surfaces.includes(surface)
                    "
                    @change="toggleSurface(selectedTeeth[0], surface)"
                  />
                  {{ surface }}
                </label>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Observações</label>
              <textarea
                class="form-input"
                v-model="selectedTeeth[0].notes"
                @blur="saveToothData(selectedTeeth[0])"
                rows="3"
                placeholder="Digite observações sobre o dente..."
              ></textarea>
            </div>
            <div class="form-actions">
              <button class="btn btn-neutral" @click="cancelSelection">Fechar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import toothHealthy from '@/assets/images/tooth/tooth-healthy.svg';
import toothCarious from '@/assets/images/tooth/tooth-carious.svg';
import toothRestored from '@/assets/images/tooth/tooth-restored.svg';
import toothCrown from '@/assets/images/tooth/tooth-crown.svg';
import toothRootCanal from '@/assets/images/tooth/tooth-root-canal.svg';
import toothMissing from '@/assets/images/tooth/tooth-missing.svg';
import ToothItem from './ToothItem.vue';
import { transformApiDataToOdontograma } from '../helpers/odontogramaHelpers';

export default {
  name: 'OdontogramForm',
  components: {
    ToothItem,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      toothHealthy,
      toothCarious,
      toothRestored,
      toothCrown,
      toothRootCanal,
      toothMissing,
      selectedTeeth: [],
      selectedTool: 'healthy',
      toothSurfaces: ['Oclusal', 'Mesial', 'Lingual', 'Distal', 'Vestibular'],
      allTeeth: [],
      generalNotes: '',
      loading: false,
    };
  },
  mounted() {
    this.loadGeneralNotes();
  },
  computed: {
    computedData() {
      if (this.data && this.data.inputs) {
        const hasInputs = Array.isArray(this.data.inputs)
          ? this.data.inputs.length > 0
          : Object.keys(this.data.inputs).length > 0;

        if (hasInputs) {
          return transformApiDataToOdontograma(this.data);
        }
      }
      return [];
    },
    upperTeeth() {
      return this.allTeeth.filter(
        tooth => this.getToothArch(tooth.number) === 'upper'
          && this.getToothType(tooth.number) === 'permanent',
      );
    },
    lowerTeeth() {
      return this.allTeeth.filter(
        tooth => this.getToothArch(tooth.number) === 'lower'
          && this.getToothType(tooth.number) === 'permanent',
      );
    },
    upperDeciduous() {
      return this.allTeeth.filter(
        tooth => this.getToothArch(tooth.number) === 'upper'
          && this.getToothType(tooth.number) === 'deciduous',
      );
    },
    lowerDeciduous() {
      return this.allTeeth.filter(
        tooth => this.getToothArch(tooth.number) === 'lower'
          && this.getToothType(tooth.number) === 'deciduous',
      );
    },
  },
  watch: {
    computedData: {
      immediate: true,
      handler(newData) {
        if (newData && newData.length > 0) {
          this.allTeeth = JSON.parse(JSON.stringify(newData));
        }
      },
    },
    selectedTeeth: {
      handler() {
        this.$forceUpdate();
      },
      deep: true,
    },
    'selectedTeeth.0.condition': {
      handler(newCondition, oldCondition) {
        if (this.selectedTeeth.length > 1 && newCondition && newCondition !== oldCondition) {
          this.applyConditionToAllSelected(newCondition);
        }
      },
    },
    data: {
      immediate: true,
      deep: true,
      handler() {
        this.loadGeneralNotes();
      },
    },
  },
  methods: {
    getToothType(toothNumber) {
      const permanentTeeth = [
        11, 12, 13, 14, 15, 16, 17, 18, 21, 22, 23, 24, 25, 26, 27, 28, 31, 32, 33, 34, 35, 36, 37,
        38, 41, 42, 43, 44, 45, 46, 47, 48,
      ];
      return permanentTeeth.includes(toothNumber) ? 'permanent' : 'deciduous';
    },
    getToothArch(toothNumber) {
      const upperTeeth = [
        11, 12, 13, 14, 15, 16, 17, 18, 21, 22, 23, 24, 25, 26, 27, 28, 51, 52, 53, 54, 55, 61, 62,
        63, 64, 65,
      ];
      return upperTeeth.includes(toothNumber) ? 'upper' : 'lower';
    },
    selectTool(tool) {
      this.selectedTool = tool;
      if (this.selectedTeeth.length > 0) {
        this.applyConditionToAllSelected(tool);
      }
    },
    selectTooth(tooth) {
      const index = this.selectedTeeth.findIndex(t => t.number === tooth.number);
      if (index === -1) {
        this.selectedTeeth.push(tooth);
      } else {
        this.selectedTeeth.splice(index, 1);
      }
    },
    applyConditionToTooth(tooth, condition) {
      tooth.condition = condition;
      this.saveToothData(tooth);
    },
    openToothDetails(tooth) {
      this.selectedTeeth = [tooth];
    },
    applyConditionToAllSelected(condition) {
      this.selectedTeeth.forEach((tooth) => {
        tooth.condition = condition;
        this.saveToothData(tooth);
      });
    },
    saveAllSelectedTeeth() {
      this.selectedTeeth.forEach((tooth) => {
        this.saveToothData(tooth);
      });
    },
    toggleSurface(tooth, surface) {
      if (!tooth.surfaces) {
        this.$set(tooth, 'surfaces', []);
      }

      const index = tooth.surfaces.indexOf(surface);

      if (index === -1) {
        tooth.surfaces.push(surface);
      } else {
        tooth.surfaces.splice(index, 1);
      }

      this.saveToothData(tooth);
    },
    cancelSelection() {
      this.selectedTeeth = [];
    },
    saveToothData(tooth) {
      if (!tooth) return;

      const surfacesValue = tooth.surfaces && tooth.surfaces.length > 0 ? tooth.surfaces.join(', ') : '';

      this.$emit('change', { name: `tooth${tooth.number}Surfaces`, value: surfacesValue });
      this.$emit('change', { name: `tooth${tooth.number}Condition`, value: tooth.condition || '' });
      this.$emit('change', { name: `tooth${tooth.number}Notes`, value: tooth.notes || '' });
    },
    onGeneralNotesChange(value) {
      this.$emit('change', { name: 'content', value });
    },
    loadGeneralNotes() {
      if (this.data && this.data.inputs) {
        let contentValue = '';

        if (Array.isArray(this.data.inputs)) {
          const contentInput = this.data.inputs.find(input => input.name === 'content');
          if (contentInput) {
            contentValue = contentInput.value || '';
          }
        } else if (typeof this.data.inputs === 'object') {
          contentValue = this.data.inputs.content || '';
        }

        this.generalNotes = contentValue;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/_variables";

.odontogram-form {
  font-size: 14px;
  line-height: 1.5;
  color: $body-font-color;
  position: relative;

  .general-notes-section {
    margin-bottom: $layout-spacing;

    .form-label {
      margin-bottom: $layout-spacing-sm;
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    border-radius: 8px;

    .loading-spinner {
      text-align: center;

      .spinner {
        width: 50px;
        height: 50px;
        margin: 0 auto 16px;
        border: 4px solid $border-color-dark;
        border-top: 4px solid $primary-color;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      .loading-text {
        font-size: 16px;
        font-weight: 600;
        color: $dark-color;
        margin: 0;
      }
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .odontograma-container {
    display: grid;
    grid-template-columns: auto 1fr 250px;
    gap: 20px;
    min-height: 600px;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr auto;
    }
  }

  .tools-sidebar {
    background: $bg-color-light;
    border-radius: 8px;
    border: 1px solid $border-color-dark;
    padding: 20px 10px;

    .tool-group {
      .tool-title {
        font-size: 12px;
        font-weight: 600;
        color: $dark-color;
        margin: 0 0 15px 0;
        text-align: center;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .tool-buttons {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .tool-btn {
          width: 40px;
          height: 40px;
          border: 1px solid $border-color-dark;
          border-radius: 6px;
          background: $bg-color-light;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover {
            border-color: $primary-color;
            background: lighten($primary-color, 45%);
          }

          &.active {
            border-color: $primary-color;
            border-width: 2px;
          }

          .tool-icon {
            width: 24px;
            height: 24px;
          }
        }
      }
    }
  }

  .lower-arch {
    margin-top: 32px;
  }

  .main-odontograma {
    background: $bg-color-light;
    border-radius: 8px;
    border: 1px solid $border-color-dark;
    padding: 32px;

    .dental-chart {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;

      .dental-arch {
        display: flex;
        gap: 8px;
        align-items: center;

        .tooth-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s ease;

          .tooth-checkbox {
            margin-bottom: 5px;

            input[type="checkbox"] {
              width: 16px;
              height: 16px;
            }
          }

          .tooth-number {
            font-size: 12px;
            font-weight: 700;
            color: $dark-color;
            margin-bottom: 8px;
          }

          .tooth-illustration {
            width: 40px;
            height: 40px;
            position: relative;
            margin-bottom: 10px;
            display: flex;
            align-items: center;
            justify-content: center;

            .tooth-svg {
              width: 40px;
              height: 40px;
            }

            .upper-arch & {
              transform: rotate(180deg);
            }

            &.selected {
              box-shadow: 0 0 0 2px $primary-color;
            }
          }

          .tooth-surfaces {
            .surface-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 2px;
              width: 40px;

              .surface {
                width: 18px;
                height: 18px;
                border: 1px solid $border-color-dark;
                border-radius: 2px;
                background: $bg-color-light;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover {
                  background: lighten($primary-color, 45%);
                  border-color: $primary-color;
                }

                &.active {
                  background: $primary-color;
                  border-color: $primary-color;
                }
              }
            }
          }
        }
      }

      .deciduous-teeth {
        display: flex;
        gap: 6px;
        align-items: center;

        &.upper-deciduous {
          margin-top: 10px;
        }

        &.lower-deciduous {
          margin-bottom: 10px;
        }

        .tooth-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s ease;

          .tooth-checkbox {
            margin-bottom: 5px;

            input[type="checkbox"] {
              width: 16px;
              height: 16px;
            }
          }

          .tooth-number {
            font-size: 12px;
            font-weight: 700;
            color: $dark-color;
            margin-bottom: 8px;
          }

          .tooth-illustration {
            width: 40px;
            height: 40px;
            position: relative;
            margin-bottom: 10px;
            display: flex;
            align-items: center;
            justify-content: center;

            .tooth-svg {
              width: 40px;
              height: 40px;
            }

            &.selected {
              border-color: $primary-color;
              box-shadow: 0 0 0 2px lighten($primary-color, 40%);
            }
          }

          .tooth-surfaces {
            .surface-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 2px;
              width: 40px;

              .surface {
                width: 18px;
                height: 18px;
                border: 1px solid $border-color-dark;
                border-radius: 2px;
                background: $bg-color-light;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover {
                  background: lighten($primary-color, 45%);
                  border-color: $primary-color;
                }

                &.active {
                  background: $primary-color;
                  border-color: $primary-color;
                }
              }
            }
          }
        }
      }

      .central-line {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 20px 0;
        position: relative;

        &::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(
            to right,
            transparent 0%,
            $border-color-dark 20%,
            $border-color-dark 80%,
            transparent 100%
          );
        }

        .central-label {
          background: $bg-color-light;
          padding: 8px 16px;
          border: 1px solid $border-color-dark;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 600;
          color: $gray-color-dark;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          position: relative;
          z-index: 1;
        }
      }
    }
  }

  .details-sidebar {
    background: $bg-color-light;
    border-radius: 8px;
    border: 1px solid $border-color-dark;
    padding: 20px;
    overflow-y: auto;

    .tooth-details {
      .details-header {
        background: $bg-color-light;
        padding: 16px 20px;
        border-bottom: 1px solid $border-color-dark;
        margin: -20px -20px 20px -20px;
        border-radius: 8px 8px 0 0;

        .details-title {
          font-size: 16px;
          font-weight: 600;
          margin: 0;
          color: $dark-color;
        }
      }

      .details-form {
        .form-group {
          margin-bottom: 20px;

          &:last-child {
            margin-bottom: 0;
          }

          .form-label {
            display: block;
            font-size: 14px;
            font-weight: 600;
            color: $body-font-color;
            margin-bottom: 8px;
          }

          .form-select,
          .form-input {
            width: 100%;
            font-size: 14px;
            border: 1px solid $border-color-dark;
            border-radius: 6px;
            transition: all 0.2s ease;
            background-color: $bg-color-light;

            &:focus {
              outline: none;
              border-color: $primary-color;
            }
          }

          .form-input {
            resize: vertical;
            min-height: 80px;
            font-family: inherit;
          }

          .surface-options {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;

            .surface-option {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 13px;
              cursor: pointer;

              input[type="checkbox"] {
                width: 16px;
                height: 16px;
              }
            }
          }
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.odontogram-form {
  .content-editor {
    .ck-editor__editable_inline {
      min-height: 5.5rem !important;
    }
  }
}
</style>
